import React, {useEffect, useState} from 'react'
import {
    Alert,
    Box,
    Button,
    Divider,
    Link,
    Modal,
    Stack,
    Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import {useNavigate} from "react-router-dom"
import {CompletionRequestJson} from "../../api/json/CompletionRequestJson"
import RowJson from "../../api/json/RowJson"
import {useProjectData} from "../../hooks/useProjectData"
import TemplateRenderer from "../templates/TemplateRenderer"
import TemplateRendererSection from "../templates/TemplateRendererSection"
import {DisplayFormat} from "../../api/enum/DisplayFormat"
import {TimeUtil} from "../../library/TimeUtil"
import CreateRowModal from "../rows/CreateRowModal"
import {RowOrigin} from "../../api/enum/RowOrigin"
import {CompletionResultJson} from "../../api/json/CompletionResultJson"

interface CompletionDetailsModalProps {
    open: boolean
    onClose: () => void
    request: CompletionRequestJson | null
}

function formatLatency(ms: number | null): string {
    if (ms == null) return ""
    if (ms < 1000) return `${ms} ms`
    return `${(ms / 1000).toFixed(1)} s`
}

function CompletionDetailsModal({open, onClose, request}: CompletionDetailsModalProps) {
    const {id: projectId, project, reloadProject} = useProjectData()
    const navigate = useNavigate()

    // First result drives the Save-to-dataset footer. Most requests return a single result.
    const firstResult: CompletionResultJson | undefined = request?.results?.[0]
    const [savedExample, setSavedExample] = useState<RowJson | null>(firstResult?.row ?? null)
    const [createModalOpen, setCreateModalOpen] = useState(false)

    // Reset local save state when the viewed request changes.
    useEffect(() => {
        setSavedExample(firstResult?.row ?? null)
    }, [firstResult?.id])

    if (!request) {
        return <></>
    }

    const modelDetailPathComponent = "completion" in request.model ? "custom-models" : "models"

    // Assemble the meta strip as a list of items; nulls are filtered out and joined with middle-dots.
    type MetaItem = { key: string, node: React.ReactNode }
    const metaItems: MetaItem[] = []
    metaItems.push({key: 'time', node: TimeUtil.ago(request.created)})
    metaItems.push({
        key: 'model',
        node: (
            <Link component="button"
                  type="button"
                  underline="hover"
                  sx={{fontSize: 'inherit', color: 'inherit', fontWeight: 500}}
                  onClick={() => {
                      onClose()
                      navigate(`/projects/${projectId}/${modelDetailPathComponent}/${request.model.id}`)
                  }}>
                {request.model.name}
            </Link>
        ),
    })
    if (request.user) {
        metaItems.push({key: 'user', node: request.user.fullName})
    }
    metaItems.push({key: 'temp', node: `Temp ${request.temperature}`})
    if (request.latency != null) {
        metaItems.push({key: 'latency', node: formatLatency(request.latency)})
    }

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '92vw',
                    maxWidth: 720,
                    maxHeight: '90vh',
                    backgroundColor: '#f7f7f7',
                    borderRadius: '6px',
                    outline: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    <Stack direction="row"
                           alignItems="center"
                           justifyContent="space-between"
                           sx={{px: 3, py: 2, backgroundColor: '#fff'}}>
                        <Typography variant="h3" fontSize={18} fontWeight={500} sx={{color: '#555'}}>
                            Completion
                        </Typography>
                    </Stack>

                    {/* Meta strip — one row of uniform muted caption text separated by middle-dots. */}
                    <Stack direction="row"
                           alignItems="center"
                           flexWrap="wrap"
                           sx={{
                               px: 3,
                               py: 1,
                               backgroundColor: '#fafafa',
                               borderTop: '1px solid #eee',
                               borderBottom: '1px solid #eee',
                               rowGap: 0.25,
                               columnGap: 1,
                           }}>
                        {metaItems.map((item, idx) => (
                            <React.Fragment key={item.key}>
                                {idx > 0 &&
                                    <Typography variant="caption"
                                                color="text.secondary"
                                                sx={{userSelect: 'none'}}>·</Typography>
                                }
                                <Typography variant="caption" color="text.secondary" component="span">
                                    {item.node}
                                </Typography>
                            </React.Fragment>
                        ))}
                    </Stack>

                    <Box sx={{px: 3, py: 3, overflowY: 'auto', flex: '1 1 auto'}}>
                        <Typography variant="overline" color="text.secondary" sx={{letterSpacing: 0.8}}>
                            Input
                        </Typography>
                        <Box sx={{
                            mt: 0.5,
                            backgroundColor: '#fff',
                            borderRadius: 1,
                            border: '1px solid #ececec',
                            px: 2,
                            py: 1.5,
                        }}>
                            <TemplateRenderer templateParts={request.model.template}
                                              dataSource={request.inputs}
                                              section="prompt"
                                              staticSystemPromptTreatment="hide"/>
                        </Box>

                        <Typography variant="overline"
                                    color="text.secondary"
                                    sx={{letterSpacing: 0.8, display: 'block', mt: 3}}>
                            Output
                        </Typography>
                        <Box sx={{
                            mt: 0.5,
                            backgroundColor: '#fff',
                            borderRadius: 1,
                            border: '1px solid #ececec',
                            px: 2,
                            py: 1.5,
                        }}>
                            {request.failed &&
                                <Alert severity="error">{request.failedReason}</Alert>
                            }

                            {request.succeeded && request.results.length > 0 &&
                                <Stack spacing={2} sx={{whiteSpace: 'pre-wrap'}}>
                                    {request.results.map((result, idx) => (
                                        <React.Fragment key={result.id}>
                                            {idx > 0 && <Divider/>}
                                            <Box>
                                                {request.results.length > 1 &&
                                                    <Typography variant="caption"
                                                                color="text.secondary"
                                                                sx={{display: 'block', mb: 0.5}}>
                                                        Result {idx + 1}
                                                    </Typography>
                                                }
                                                <TemplateRendererSection
                                                    value={result.text}
                                                    display={DisplayFormat.PLAIN_TEXT}
                                                    dynamicSize={false}/>
                                            </Box>
                                        </React.Fragment>
                                    ))}
                                </Stack>
                            }

                            {!request.failed && !request.succeeded &&
                                <Typography variant="body2" color="text.secondary">
                                    {request.started ? 'Running…' : 'Queued'}
                                </Typography>
                            }
                        </Box>
                    </Box>

                    {project && request.succeeded && firstResult &&
                        <>
                            <Divider/>
                            <Stack direction="row"
                                   spacing={1}
                                   justifyContent="flex-end"
                                   sx={{px: 3, py: 2, backgroundColor: '#fff'}}>
                                <Button variant="outlined" color="secondary" onClick={onClose}>
                                    Close
                                </Button>
                                {savedExample ? (
                                    <Button variant="contained"
                                            startIcon={<EditIcon/>}
                                            onClick={() => setCreateModalOpen(true)}>
                                        Edit saved row
                                    </Button>
                                ) : (
                                    <Button variant="contained"
                                            startIcon={<AddIcon/>}
                                            onClick={() => setCreateModalOpen(true)}>
                                        Save to dataset
                                    </Button>
                                )}
                            </Stack>
                        </>
                    }
                </Box>
            </Modal>

            {project && firstResult &&
                <CreateRowModal
                    key={savedExample?.id ?? firstResult.id}
                    open={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                    project={project}
                    row={savedExample ?? undefined}
                    origin={RowOrigin.PLAYGROUND}
                    generativeResultId={firstResult.id}
                    referenceValues={{...request.inputs, ...firstResult.referenceValues}}
                    didCreate={(_, row) => {
                        setSavedExample(row)
                        setCreateModalOpen(false)
                        reloadProject()
                    }}
                    didUpdate={(row) => {
                        setSavedExample(row)
                        setCreateModalOpen(false)
                    }}
                    didDelete={() => {
                        setSavedExample(null)
                        setCreateModalOpen(false)
                    }}
                />
            }
        </>
    )
}

export default CompletionDetailsModal
